import { metrics } from 'bc-metrics';
import { createRoot } from 'react-dom/client';
import { injectIntl } from 'react-intl';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { BootstrapThemeProvider } from 'bc-ods';
import App from 'components/App';
import AppErrorBoundary from 'components/AppErrorBoundary';
import 'index.scss';
import IntlContext from 'IntlContext';
import 'bc-ods/src/styles/index.scss';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import LanguageProvider from 'i18n/LanguageProvider';
import clickstream from 'clickstream';
import { getBasePath } from 'utils/url-utils';
import { reloadFeatureFlags } from './featureFlags';
import { ClickstreamEventEnum } from 'bc-model';
import { loadApiClientEnv } from 'bc-artifacts-client/api';

void reloadFeatureFlags();

void loadApiClientEnv();

// Clean up old service workers.
// We will want to remove this when we re-implement service workers.
if ('serviceWorker' in navigator) {
  void navigator.serviceWorker.getRegistrations().then((registrations) => {
    registrations.forEach((registration) => {
      void registration.unregister();
    });
  });
}

// turn the old context into the new context
const InjectIntlContext: any = injectIntl(({ intl, children }) => (
  <IntlContext.Provider value={intl}>{children as any}</IntlContext.Provider>
));

const container = document.getElementById('root')!;
const root = createRoot(container);

// fixes https://github.com/emotion-js/emotion/issues/1105
const cache = createCache({ key: 'onebrief', speedy: true });
cache.compat = true;
const router = createBrowserRouter(
  [
    {
      path: '*',
      element: (
        <InjectIntlContext>
          <AppErrorBoundary>
            <App />
          </AppErrorBoundary>
        </InjectIntlContext>
      ),
    },
  ],
  { basename: getBasePath() }
);

root.render(
  <CacheProvider value={cache}>
    <BootstrapThemeProvider>
      <LanguageProvider>
        <RouterProvider router={router} />
      </LanguageProvider>
    </BootstrapThemeProvider>
  </CacheProvider>
);

window.addEventListener('error', (event) => {
  const err = event.error instanceof Error ? event.error : new Error(event.message);

  metrics.logError(err, {
    errorType: 'Exception Unhandled',
  });
  clickstream.track(ClickstreamEventEnum.EXCEPTION_UNHANDLED, {
    message: err.message,
    name: err.name,
    stack: err.stack,
  });
});
